/**
 * Submission — what actually happens when the surveyor taps Submit (spec §2.5).
 *
 * The order is the whole design:
 *
 *   1. run the engines      — `detectGaps()` and `validate()`, over the document
 *                             as it stands on this phone
 *   2. decide the status    — `surveyStatusFor()`, never a second opinion
 *   3. PERSIST LOCALLY      — status, gaps and submittedAt written to IndexedDB
 *   4. queue the submission — one outbox row; the sync engine carries it, and
 *                             the server fires `survey_submitted` when it lands
 *
 * Step 3 before step 4 is not a style preference. A surveyor submits from a pit
 * with no signal (brief §6.6): the record has to be a submitted record on the
 * device the instant the button is released, whether or not anything ever
 * reaches the network. The outbox row is the promise to tell the office, and it
 * survives a force-quit because it is a row in the same database.
 *
 * ── The surveyor can never self-clear a gap (§2.3) ───────────────────────────
 * Nothing here removes a gap and nothing here refuses to submit because of one.
 * Gaps are collected, written onto the record, counted in the button's caption,
 * and sent. Chip and Tim decide what to do about them.
 *
 * That includes the D-017 acknowledgements. The last look decides WHEN Submit is
 * offered (see `reckoning.ts` and `CloseScreen`); by the time this module runs,
 * every acknowledgement does exactly one thing — it attaches the surveyor's
 * reason to the gap it belongs to, as `surveyorNote`. The gap itself is
 * untouched, and still blocking.
 *
 * Pure: no store, no clock, no React. The caller supplies the instant and owns
 * the transaction.
 */

import type { Gap, SurveyDocument, SurveyStatus, ValidationIssue } from '../../core/types';
import { BLOCKING_GAP_REASONS, detectGaps, validate } from '../../core/validation';
import { applyAcknowledgements } from './reckoning';
import { computeResults, type SurveyResults } from './results';

export interface SubmissionPlan {
  /**
   * Every gap that will travel with the record: the engine's, from this
   * phone's answers alone (round 8 ruling 1) — `doc.gaps` is what the last
   * submit sent and is never merged back in, so a stale gap cannot travel twice.
   */
  gaps: Gap[];
  /** The ones that make the survey `submitted_with_gaps` (§4 rule 11). */
  blocking: Gap[];
  /** Recommended shots, listed, never blocking (§4 rule 8) — excludes required shots. */
  recommendedPhotoGaps: Gap[];
  issues: ValidationIssue[];
  status: SurveyStatus;
}

/**
 * The plan, read off results the screen is already showing.
 *
 * Taking it from `SurveyResults` rather than recomputing means the number in
 * the button ("1 gap flagged") is by construction the number in the list above
 * it and the number written to the record — they are one array, not three
 * agreeing calculations.
 */
export function planFrom(results: SurveyResults): SubmissionPlan {
  /*
   * D-017. The surveyor's reason for a gap they could not close — "the pit was
   * locked" — is attached HERE, on the way out, and nowhere else. It changes no
   * gap's id, reason or blocking status: an acknowledged gap is still a gap,
   * still counted in the button's caption, still written onto the record, and
   * still Chip's and Tim's to resolve (§2.3). All that is added is the note,
   * which `renderGap` already prints on its own line in the notification.
   *
   * `applyAcknowledgements` hands the same array back when there is nothing to
   * attach, so a survey with no acknowledgements is byte-for-byte the plan it
   * was before this line existed.
   */
  const gaps = applyAcknowledgements(results.doc, results.gaps);
  return {
    gaps,
    blocking: gaps.filter((gap) => BLOCKING_GAP_REASONS.includes(gap.reason)),
    recommendedPhotoGaps: gaps.filter((gap) => gap.reason === 'missing_photo'),
    issues: results.issues,
    status: results.projectedStatus,
  };
}

/**
 * The same plan from a bare document — for tests, and for any caller that does
 * not already hold results. `detectGaps` and `validate` are both named here on
 * purpose: `detectGaps` runs the rules internally, but "submit runs detectGaps
 * and validate" is a contract, and a contract that is only satisfied by an
 * implementation detail of another module is one refactor from being false.
 */
export function planSubmission(doc: SurveyDocument): SubmissionPlan {
  void detectGaps(doc);
  void validate(doc);
  return planFrom(computeResults(doc));
}

/**
 * Stamp the plan onto the document, in place — the mutator `setStructure` wants.
 * This is the local persistence step; the store commits it inside the same
 * transaction that queues the document push.
 *
 * `submittedByName` is the surveyor's own name off `identity` (D-038, ruling
 * 11) — trimmed, and stamped only when it is actually given and non-empty. A
 * caller with no identity (an unlinked device) passes nothing, and the field
 * is left off the document exactly as it already is for every record sealed
 * before this existed.
 */
export function applySubmission(
  document: SurveyDocument,
  plan: SubmissionPlan,
  submittedAt: string,
  submittedByName?: string,
): void {
  document.gaps = plan.gaps;
  document.status = plan.status;
  document.submittedAt = submittedAt;
  const name = submittedByName?.trim();
  if (name) document.submittedByName = name;
}

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? '' : 's'}`;
}

/**
 * The line under the Submit button. It states the consequence — how many gaps
 * are going with it and that it uploads later — because a surveyor with no
 * signal must not be left wondering whether the tap did anything.
 */
export function submitConsequence(plan: SubmissionPlan): string {
  const flagged =
    plan.blocking.length > 0 ? `${plural(plan.blocking.length, 'gap')} flagged` : 'Clean';
  return `${flagged} · uploads when you have signal`;
}

/**
 * "6 Aug 2026" from a stored ISO instant.
 *
 * Formatted with the device's own locale data and nothing else — no clock is
 * read here, and an unparseable timestamp yields nothing rather than "Invalid
 * Date" on a submitted record. Moved here from `CloseScreen.tsx` (round 7,
 * task 9) so `sealedLine` — a pure function — can be built on it without the
 * screen in between.
 */
export function submittedDay(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  return at.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * The sealed face's one line: who submitted this record, and when (round 7,
 * controller ruling 11). It replaces `submittedSummary` — the status/gap-count
 * sentence was itself replaced by the rows above it (D-033 R8 had already cut
 * its "Chip & Tim decide" / "Straight to estimating" tails down to the bare
 * state; the design authority then asked for the state to be the surveyor and
 * the date instead).
 *
 * `submittedByName` is optional on every record sealed before this existed —
 * and on one sealed from an unlinked device today — so the line degrades to
 * the date alone rather than inventing a name.
 */
export function sealedLine(doc: Pick<SurveyDocument, 'submittedAt' | 'submittedByName'>): string {
  const day = doc.submittedAt ? submittedDay(doc.submittedAt) : '';
  const name = doc.submittedByName?.trim();
  if (name) return day ? `Submitted by ${name} · ${day}` : `Submitted by ${name}`;
  return day ? `Submitted · ${day}` : 'Submitted';
}
